/** Flat two-panel shell for sign in / sign up: Owlbert on a purple side panel, the form on paper. */
import { ReactNode, useState } from "react";
import { useLocation } from "wouter";
import { Input } from "@/components/ui/input";
import { Illustration, type IllustrationName } from "@/components/Illustration";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

export interface AuthFeature { icon: ReactNode; title: string; text: string; testId?: string }

export function AuthShell({ illustration, headline, accent, subtitle, features = [], stats = [], children, formTitle, formSubtitle, footer }: {
  illustration: IllustrationName; headline: string; accent?: string; subtitle: string; features?: AuthFeature[];
  stats?: Array<{ value: string; label: string; testId?: string }>; children: ReactNode; formTitle: string; formSubtitle?: string; footer?: ReactNode;
}) {
  const [, setLocation] = useLocation();
  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Side panel */}
      <aside className="bg-primary text-primary-foreground lg:w-[46%] xl:w-1/2 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 lg:px-10 lg:py-6">
          <button type="button" onClick={() => setLocation("/")} className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-sm" data-testid="link-back-home" aria-label="Back to home">
            <img src={logoUrl} alt="EduFocal" className="h-6 w-auto" data-testid="img-login-logo" />
          </button>
          <button type="button" onClick={() => setLocation("/")} className="hidden lg:inline-flex items-center gap-1.5 text-sm text-primary-foreground/80 hover:text-primary-foreground"><ArrowLeft className="w-4 h-4" />Back to home</button>
        </div>
        <div className="flex-1 px-5 pb-6 lg:px-10 lg:pb-10 flex flex-col justify-center gap-6 lg:gap-8">
          <div className="flex items-center gap-4 lg:block">
            <div className="shrink-0 lg:mb-6"><Illustration name={illustration} size={96} className="lg:hidden" /><Illustration name={illustration} size={168} className="hidden lg:inline-block" /></div>
            <div>
              <h1 className="text-2xl lg:text-4xl xl:text-5xl font-bold tracking-tight leading-tight" data-testid="text-signup-headline">
                {headline}{accent && <> <span className="text-amber-300">{accent}</span></>}
              </h1>
              <p className="mt-2 lg:mt-4 text-sm lg:text-lg text-primary-foreground/85 max-w-md" data-testid="text-signup-subtitle">{subtitle}</p>
            </div>
          </div>
          {features.length > 0 && (
            <ul className="hidden lg:flex flex-col gap-4">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={f.testId}>
                  <div className={`w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0 ${i % 2 ? "rotate-2" : "-rotate-2"}`}>{f.icon}</div>
                  <div><div className="font-semibold">{f.title}</div><div className="text-sm text-primary-foreground/80">{f.text}</div></div>
                </li>
              ))}
            </ul>
          )}
          {stats.length > 0 && (
            <div className="hidden lg:grid grid-cols-3 gap-3">
              {stats.map((st, i) => (
                <div key={i} className="rounded-xl bg-white/15 px-3 py-3" data-testid={st.testId}>
                  <div className="text-2xl font-bold tabular-nums leading-none">{st.value}</div>
                  <div className="text-xs text-primary-foreground/80 mt-1">{st.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex items-start lg:items-center justify-center px-4 py-6 sm:px-6 lg:px-12 lg:py-10">
        <div className="w-full max-w-md">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="text-form-title">{formTitle}</h2>
          {formSubtitle && <p className="mt-1 text-muted-foreground" data-testid="text-form-subtitle">{formSubtitle}</p>}
          <div className="mt-6">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-muted-foreground space-y-2">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

/** Password field with a show/hide toggle. Same props as Input. */
export function PasswordInput(props: React.ComponentProps<typeof Input>) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={show ? "text" : "password"} className={`h-12 text-base rounded-lg pr-12 ${props.className ?? ""}`} />
      <button type="button" onClick={() => setShow(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground" aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export const fieldClass = "h-12 text-base rounded-lg";
